import React from 'react';
import { Link } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import { useSubscription } from '../contexts/SubscriptionContext';

const DashboardPage: React.FC = () => {
  const { subscription } = useSubscription();

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 欢迎区域 */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                欢迎回来！
              </h1>
              <p className="text-gray-600">开始您的AI短剧创作之旅</p>
            </div>
            <div className="text-right">
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                subscription?.plan === 'pro' ? 'bg-blue-100 text-blue-800' :
                subscription?.plan === 'enterprise' ? 'bg-purple-100 text-purple-800' :
                'bg-green-100 text-green-800'
              }`}>
                {subscription?.plan === 'pro' ? '专业版' :
                 subscription?.plan === 'enterprise' ? '企业版' : '免费版'}
              </span>
              <p className="text-sm text-gray-500 mt-1">
                到期时间：{subscription?.currentPeriodEnd.toLocaleDateString('zh-CN')}
              </p>
            </div>
          </div>
        </div>

        {/* 统计卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">剩余生成次数</p>
                <p className="text-2xl font-bold text-gray-900">
                  {subscription?.features?.maxScriptsPerMonth === Infinity ? '∞' : subscription?.features?.maxScriptsPerMonth || 3}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <span className="text-purple-600 text-xl">🎭</span>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">项目总数</p>
                <p className="text-2xl font-bold text-gray-900">12</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <span className="text-blue-600 text-xl">📁</span>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">角色总数</p>
                <p className="text-2xl font-bold text-gray-900">8</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <span className="text-green-600 text-xl">👥</span>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">本月创作</p>
                <p className="text-2xl font-bold text-gray-900">5</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <span className="text-orange-600 text-xl">📈</span>
              </div>
            </div>
          </div>
        </div>

        {/* 功能权限 */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">您的功能权限</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className={`p-4 rounded-lg border ${
              subscription?.features?.maxScriptsPerMonth !== undefined ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
            }`}>
              <div className="flex items-center mb-2">
                <span className={`text-lg mr-2 ${
                  subscription?.features?.maxScriptsPerMonth !== undefined ? 'text-green-600' : 'text-gray-400'
                }`}>
                  {subscription?.features?.maxScriptsPerMonth !== undefined ? '✅' : '❌'}
                </span>
                <span className="font-medium">剧本生成</span>
              </div>
              <p className="text-sm text-gray-600">
                {subscription?.features?.maxScriptsPerMonth === Infinity ? '无限生成机会' :
                 subscription?.features?.maxScriptsPerMonth ? `每月${subscription.features.maxScriptsPerMonth}次生成机会` : '每月3次生成机会'}
              </p>
            </div>

            <div className={`p-4 rounded-lg border ${
              subscription?.features?.maxCharacters !== undefined ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
            }`}>
              <div className="flex items-center mb-2">
                <span className={`text-lg mr-2 ${
                  subscription?.features?.maxCharacters !== undefined ? 'text-green-600' : 'text-gray-400'
                }`}>
                  {subscription?.features?.maxCharacters !== undefined ? '✅' : '❌'}
                </span>
                <span className="font-medium">角色管理</span>
              </div>
              <p className="text-sm text-gray-600">
                {subscription?.features?.maxCharacters === Infinity ? '无限角色数量' :
                 subscription?.features?.maxCharacters ? `最多${subscription.features.maxCharacters}个角色` : '最多5个角色'}
              </p>
            </div>

            <div className={`p-4 rounded-lg border ${
              subscription?.features?.hasAICharacterGeneration ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
            }`}>
              <div className="flex items-center mb-2">
                <span className={`text-lg mr-2 ${
                  subscription?.features?.hasAICharacterGeneration ? 'text-green-600' : 'text-gray-400'
                }`}>
                  {subscription?.features?.hasAICharacterGeneration ? '✅' : '❌'}
                </span>
                <span className="font-medium">AI角色生成</span>
              </div>
              <p className="text-sm text-gray-600">智能创建角色形象</p>
            </div>

            <div className={`p-4 rounded-lg border ${
              subscription?.features?.hasAdvancedStoryboard ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
            }`}>
              <div className="flex items-center mb-2">
                <span className={`text-lg mr-2 ${
                  subscription?.features?.hasAdvancedStoryboard ? 'text-green-600' : 'text-gray-400'
                }`}>
                  {subscription?.features?.hasAdvancedStoryboard ? '✅' : '❌'}
                </span>
                <span className="font-medium">高级分镜设计</span>
              </div>
              <p className="text-sm text-gray-600">专业分镜设计功能</p>
            </div>

            <div className={`p-4 rounded-lg border ${
              subscription?.features?.hasMultipleExportFormats ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
            }`}>
              <div className="flex items-center mb-2">
                <span className={`text-lg mr-2 ${
                  subscription?.features?.hasMultipleExportFormats ? 'text-green-600' : 'text-gray-400'
                }`}>
                  {subscription?.features?.hasMultipleExportFormats ? '✅' : '❌'}
                </span>
                <span className="font-medium">多种格式导出</span>
              </div>
              <p className="text-sm text-gray-600">支持多种文件格式导出</p>
            </div>

            <div className={`p-4 rounded-lg border ${
              subscription?.features?.hasPrioritySupport ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
            }`}>
              <div className="flex items-center mb-2">
                <span className={`text-lg mr-2 ${
                  subscription?.features?.hasPrioritySupport ? 'text-green-600' : 'text-gray-400'
                }`}>
                  {subscription?.features?.hasPrioritySupport ? '✅' : '❌'}
                </span>
                <span className="font-medium">优先技术支持</span>
              </div>
              <p className="text-sm text-gray-600">优先获得技术支持</p>
            </div>
          </div>
        </div>

        {/* 快速操作 */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">快速操作</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              to="/create"
              className="p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors text-center"
            >
              <div className="text-2xl mb-2">🎭</div>
              <h3 className="font-medium text-gray-900">创建剧本</h3>
              <p className="text-sm text-gray-600">开始新的创作</p>
            </Link>

            <Link
              to="/projects"
              className="p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors text-center"
            >
              <div className="text-2xl mb-2">📁</div>
              <h3 className="font-medium text-gray-900">我的项目</h3>
              <p className="text-sm text-gray-600">管理所有项目</p>
            </Link>

            <Link
              to="/character-setting"
              className="p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors text-center"
            >
              <div className="text-2xl mb-2">👥</div>
              <h3 className="font-medium text-gray-900">角色管理</h3>
              <p className="text-sm text-gray-600">创建和编辑角色</p>
            </Link>

            <Link
              to="/analytics"
              className="p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors text-center"
            >
              <div className="text-2xl mb-2">📊</div>
              <h3 className="font-medium text-gray-900">数据统计</h3>
              <p className="text-sm text-gray-600">查看使用分析</p>
            </Link>

            <Link
              to="/pricing"
              className="p-4 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors text-center"
            >
              <div className="text-2xl mb-2">⭐</div>
              <h3 className="font-medium text-gray-900">升级套餐</h3>
              <p className="text-sm text-gray-600">解锁更多功能</p>
            </Link>
          </div>
        </div>

        {/* 升级提示 */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-800 text-white rounded-xl p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold mb-2">升级到专业版</h3>
              <p className="text-purple-100">
                解锁无限AI剧本生成、高级分镜设计、AI角色生成等强大功能
              </p>
            </div>
            <Link
              to="/pricing"
              className="px-6 py-3 bg-white text-purple-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              立即升级
            </Link>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default DashboardPage;